/**
 * Aidat presenter'i
 */

import { doc, setDoc, deleteDoc, db, COLLECTIONS, MONTHS } from '../../firebase-config.js';
import { AppState } from '../../modules/state.js';
import { formatNumber } from '../../shared/utils/formatters.js';

interface DuesPresenterDeps {
    totalApartments: number;
    formatNumber: (value: number) => string;
    showToast: (message: string, type?: string) => void;
    renderTransactions: () => void;
    updateFinanceSummary: () => void;
}

function ensureYearState(year: number, totalApartments: number): void {
    if (!AppState.dues[year]) {
        AppState.dues[year] = {};
    }

    for (let apartment = 1; apartment <= totalApartments; apartment++) {
        if (!AppState.dues[year][apartment]) {
            AppState.dues[year][apartment] = {};
        }

        for (let month = 1; month <= 12; month++) {
            if (typeof AppState.dues[year][apartment][month] !== 'boolean') {
                AppState.dues[year][apartment][month] = false;
            }
        }
    }
}

async function syncAidatTransaction(year: number, month: number, deps: DuesPresenterDeps): Promise<void> {
    const monthlyDue = AppState.settings.monthlyDueAmount || 0;
    let paidCount = 0;

    for (let apartment = 1; apartment <= deps.totalApartments; apartment++) {
        if (AppState.dues[year]?.[apartment]?.[month]) {
            paidCount++;
        }
    }

    const totalAmount = paidCount * monthlyDue;
    const monthName = MONTHS[month - 1];
    const transactionId = `aidat_${year}_${month}`;
    const transactionDate = `${year}-${String(month).padStart(2, '0')}-01`;

    const existingTransaction = AppState.transactions.find(transaction =>
        transaction.id === transactionId || transaction._docId === transactionId
    );
    const existingDocId = existingTransaction?._docId || transactionId;

    if (totalAmount > 0) {
        const transactionData = {
            id: transactionId,
            type: 'income',
            category: 'aidat',
            amount: totalAmount,
            date: transactionDate,
            description: `${monthName} ${year} Aidat Gelirleri (${paidCount} daire)`,
            isAutoGenerated: true,
            createdAt: existingTransaction ? existingTransaction.createdAt : new Date().toISOString()
        };

        if (existingTransaction) {
            const index = AppState.transactions.findIndex(transaction =>
                transaction.id === transactionId || transaction._docId === transactionId
            );

            if (index !== -1) {
                AppState.transactions[index] = { ...transactionData, _docId: existingDocId };
            }

            await setDoc(doc(db, COLLECTIONS.TRANSACTIONS, existingDocId), transactionData, { merge: true });
        } else {
            AppState.transactions.push({ ...transactionData, _docId: transactionId });
            await setDoc(doc(db, COLLECTIONS.TRANSACTIONS, transactionId), transactionData, { merge: true });
        }
    } else if (existingTransaction) {
        AppState.transactions = AppState.transactions.filter(transaction =>
            transaction.id !== transactionId && transaction._docId !== transactionId
        );
        await deleteDoc(doc(db, COLLECTIONS.TRANSACTIONS, existingDocId));
    }

    if (document.getElementById('finance-section')?.classList.contains('active')) {
        deps.renderTransactions();
        deps.updateFinanceSummary();
    }
}

export function renderDuesTablePresenter(deps: DuesPresenterDeps): void {
    const tbody = document.getElementById('dues-table-body');
    const yearSelect = document.getElementById('dues-year-select') as HTMLSelectElement | null;
    const monthlyDueInput = document.getElementById('monthly-due-amount') as HTMLInputElement | null;
    const bulkMonthEl = document.getElementById('dues-bulk-month') as HTMLSelectElement | null;
    const unpaidOnlyEl = document.getElementById('dues-unpaid-only') as HTMLInputElement | null;

    if (!tbody || !yearSelect || !monthlyDueInput) return;

    const year = parseInt(yearSelect.value, 10);
    const amount = AppState.settings.monthlyDueAmount || 0;
    const filterMonth = bulkMonthEl ? parseInt(bulkMonthEl.value, 10) : (new Date().getMonth() + 1);
    const unpaidOnly = unpaidOnlyEl?.checked || false;

    monthlyDueInput.value = String(amount);
    ensureYearState(year, deps.totalApartments);

    let totalPaid = 0;
    let totalPending = 0;
    let html = '';
    let visibleRows = 0;

    for (let apartment = 1; apartment <= deps.totalApartments; apartment++) {
        if (unpaidOnly && AppState.dues[year]?.[apartment]?.[filterMonth]) {
            continue;
        }

        visibleRows++;
        html += `<tr><td>Daire ${apartment}</td>`;

        for (let month = 1; month <= 12; month++) {
            const isPaid = AppState.dues[year]?.[apartment]?.[month] || false;
            if (isPaid) totalPaid += amount;
            else totalPending += amount;

            html += `<td><button class="due-toggle ${isPaid ? 'paid' : 'unpaid'}" onclick="toggleDue(${year}, ${apartment}, ${month})">${isPaid ? '✓' : '✗'}</button></td>`;
        }

        html += '</tr>';
    }

    if (visibleRows === 0) {
        html = '<tr><td colspan="13"><p class="empty-state">Seçilen filtreye uygun borçlu daire yok.</p></td></tr>';
    }

    tbody.innerHTML = html;

    const totalCollected = document.getElementById('total-collected');
    const totalPendingEl = document.getElementById('total-pending');
    if (totalCollected) totalCollected.textContent = `₺${deps.formatNumber(totalPaid)}`;
    if (totalPendingEl) totalPendingEl.textContent = `₺${deps.formatNumber(totalPending)}`;
}

export async function toggleDuePresenter(year: number, apartment: number, month: number, deps: DuesPresenterDeps): Promise<void> {
    ensureYearState(year, deps.totalApartments);

    const wasPaid = AppState.dues[year][apartment][month];
    AppState.dues[year][apartment][month] = !wasPaid;
    const isNowPaid = AppState.dues[year][apartment][month];

    try {
        await setDoc(doc(db, COLLECTIONS.DUES, year.toString()), AppState.dues[year], { merge: true });
    } catch (error: any) {
        AppState.dues[year][apartment][month] = wasPaid;
        renderDuesTablePresenter(deps);
        console.error('Aidat güncelleme hatası:', error);
        deps.showToast('Aidat durumu kaydedilemedi. Lütfen tekrar deneyin.', 'error');
        return;
    }

    try {
        await syncAidatTransaction(year, month, deps);
    } catch (error: any) {
        console.warn('Aidat gelir senkronu başarısız:', error);
        deps.showToast('Aidat kaydedildi, ancak gelir senkronu yapılamadı.', 'info');
    }

    renderDuesTablePresenter(deps);
    deps.showToast(`Daire ${apartment} - ${MONTHS[month - 1]} ${isNowPaid ? 'ödendi' : 'ödenmedi'}`, 'success');
}

export async function setDueStatusForMonthPresenter(isPaid: boolean, deps: DuesPresenterDeps): Promise<void> {
    const yearSelect = document.getElementById('dues-year-select') as HTMLSelectElement | null;
    const monthSelect = document.getElementById('dues-bulk-month') as HTMLSelectElement | null;
    if (!yearSelect || !monthSelect) return;

    const year = parseInt(yearSelect.value, 10);
    const month = parseInt(monthSelect.value, 10);
    ensureYearState(year, deps.totalApartments);

    const previousMonthStates: Record<number, boolean> = {};
    for (let apartment = 1; apartment <= deps.totalApartments; apartment++) {
        previousMonthStates[apartment] = !!AppState.dues[year][apartment][month];
        AppState.dues[year][apartment][month] = isPaid;
    }

    try {
        await setDoc(doc(db, COLLECTIONS.DUES, year.toString()), AppState.dues[year], { merge: true });
    } catch (error) {
        console.error('Toplu aidat güncelleme hatası:', error);
        for (let apartment = 1; apartment <= deps.totalApartments; apartment++) {
            AppState.dues[year][apartment][month] = previousMonthStates[apartment];
        }
        renderDuesTablePresenter(deps);
        deps.showToast('Toplu aidat güncellemesi kaydedilemedi.', 'error');
        return;
    }

    try {
        await syncAidatTransaction(year, month, deps);
    } catch (error) {
        console.warn('Toplu aidat gelir senkronu başarısız:', error);
        deps.showToast('Aidat kaydedildi, ancak gelir senkronu yapılamadı.', 'info');
    }

    renderDuesTablePresenter(deps);
    deps.showToast(`${MONTHS[month - 1]} ayı tüm daireler için ${isPaid ? 'ödendi' : 'ödenmedi'} olarak güncellendi`, 'success');
}

export async function updateMonthlyDueAmountPresenter(deps: DuesPresenterDeps): Promise<void> {
    const monthlyDueInput = document.getElementById('monthly-due-amount') as HTMLInputElement | null;
    if (!monthlyDueInput) return;

    AppState.settings.monthlyDueAmount = parseInt(monthlyDueInput.value, 10) || 0;

    try {
        await setDoc(doc(db, COLLECTIONS.SETTINGS, 'config'), AppState.settings, { merge: true });
    } catch (error) {
        console.error('Aidat tutarı kaydetme hatası:', error);
        deps.showToast('Aidat tutarı kaydedilemedi.', 'error');
        return;
    }

    renderDuesTablePresenter(deps);
    deps.showToast('Aylık aidat tutarı güncellendi.', 'success');
}

export const duesPresenterUtils = {
    ensureYearState,
    syncAidatTransaction,
    formatNumber
};
